import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Table, Badge, Button, Form, InputGroup, Spinner, Pagination } from 'react-bootstrap';
import { LuSearch, LuFilter, LuChevronRight, LuCalendar, LuPackage, LuCheck, LuX, LuClock } from "react-icons/lu";
import { getOrders } from '../../redux/reducers/orderSlice';
import moment from 'moment';
import { formatCurrency } from '../../utils/currency';

const OrderList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('all');
    const [page, setPage] = useState(1);

    const { orders, loading, pagination, statusCounts } = useSelector((state) => state.order);
    const currency = useSelector((state) => state.business?.currencyContext?.currency || 'CAD');

    useEffect(() => {
        dispatch(getOrders({ page, status, search }));
    }, [dispatch, page, status, search]);

    const getStatusBadge = (status) => {
        switch (status?.toUpperCase()) {
            case "PAID":
            case "DELIVERED":
                return <Badge bg="success-subtle" className="text-success rounded-pill px-3">{status === 'PAID' ? 'Paid' : 'Delivered'}</Badge>;
            case "PENDING_PAYMENT":
                return <Badge bg="warning-subtle" className="text-warning rounded-pill px-3">Pending Payment</Badge>;
            case "ACCEPTED":
                return <Badge bg="info-subtle" className="text-info rounded-pill px-3">Accepted</Badge>;
            case "PROCESSING":
                return <Badge bg="primary-subtle" className="text-primary rounded-pill px-3">Processing</Badge>;
            case "SHIPPED":
                return <Badge bg="info-subtle" className="text-info rounded-pill px-3">Shipped</Badge>;
            case "CANCELLED":
                return <Badge bg="secondary-subtle" className="text-secondary rounded-pill px-3">Cancelled</Badge>;
            case "REJECTED":
                return <Badge bg="danger-subtle" className="text-danger rounded-pill px-3">Rejected</Badge>;
            default:
                return <Badge bg="secondary-subtle" className="text-secondary rounded-pill px-3">{status?.replace('_', ' ')}</Badge>;
        }
    };

    return (
        <div className="container-fluid py-4">
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="pageTitle">
                    <h1>Orders</h1>
                </div>
                {/* <div>
                    <h4 className="fw-bold text-dark mb-1">Customer Orders</h4>
                    <p className="text-muted small mb-0">Manage incoming orders from your customer portal.</p>
                </div> */}
                <Button
                    className="rounded-3 px-4 py-2 text-white border-0 d-flex align-items-center gap-2 shadow-sm"
                    style={{ background: "#28B0C4" }}
                    onClick={() => navigate('/create-invoice?type=order')}
                >
                    <LuPackage size={18} /> New Order
                </Button>
            </div>

            <Row className="g-3 mb-4">
                {['PENDING_PAYMENT', 'PAID', 'ACCEPTED', 'PROCESSING'].map((s) => (
                    <Col key={s} md={3}>
                        <Card className="border-0 shadow-sm rounded-4 cursor-pointer" onClick={() => setStatus(s)}>
                            <Card.Body className="p-3 d-flex align-items-center gap-3">
                                <div className="rounded-3 p-2" style={{ background: status === s ? '#28B0C415' : '#f8fafc', color: '#28B0C4' }}>
                                    <LuPackage size={20} />
                                </div>
                                <div>
                                    <div className="text-muted extra-small fw-bold text-uppercase">{s.replace('_', ' ')}</div>
                                    <div className="fs-5 fw-bold text-dark">{statusCounts[s] || 0}</div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                <Card.Header className="bg-white border-0 p-4">
                    <Row className="g-3">
                        <Col md={8}>
                            <InputGroup className="bg-light border-0 rounded-3 overflow-hidden">
                                <InputGroup.Text className="bg-transparent border-0 ps-3">
                                    <LuSearch className="text-muted" size={18} />
                                </InputGroup.Text>
                                <Form.Control
                                    placeholder="Search by order number or customer name..."
                                    className="bg-transparent border-0 py-2 shadow-none"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </InputGroup>
                        </Col>
                        <Col md={4}>
                            <Form.Select
                                className="bg-light border-0 rounded-3 py-2 ps-3 shadow-none"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                <option value="all">All Status</option>
                                <option value="PENDING_PAYMENT">Pending Payment</option>
                                <option value="PAID">Paid</option>
                                <option value="ACCEPTED">Accepted</option>
                                <option value="PROCESSING">Processing</option>
                                <option value="SHIPPED">Shipped</option>
                                <option value="DELIVERED">Delivered</option>
                                <option value="CANCELLED">Cancelled</option>
                                <option value="REJECTED">Rejected</option>
                            </Form.Select>
                        </Col>
                    </Row>
                </Card.Header>

                <Card.Body className="p-0">
                    {loading && !orders.length ? (
                        <div className="text-center py-5"><Spinner animation="border" style={{ color: "#28B0C4" }} /></div>
                    ) : orders.length > 0 ? (
                        <div className="table-responsive">
                            <Table hover className="align-middle mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-4 py-3 text-muted small fw-bold">ORDER #</th>
                                        <th className="py-3 text-muted small fw-bold">CUSTOMER</th>
                                        <th className="py-3 text-muted small fw-bold">DATE</th>
                                        <th className="py-3 text-muted small fw-bold">TOTAL</th>
                                        <th className="py-3 text-muted small fw-bold">STATUS</th>
                                        <th className="pe-4 py-3"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.map((order) => (
                                        <tr key={order._id} className="cursor-pointer" onClick={() => navigate(`/orders/${order._id}`)}>
                                            <td className="ps-4 py-3">
                                                <div className="fw-bold text-dark">{order.orderNumber}</div>
                                            </td>
                                            <td className="py-3">
                                                <div className="fw-semibold text-dark small">{order.customerId?.name}</div>
                                                <div className="text-muted extra-small">{order.customerId?.email}</div>
                                            </td>
                                            <td className="py-3">
                                                <div className="text-muted small">{moment(order.createdAt).format('MMM D, YYYY')}</div>
                                            </td>
                                            <td className="py-3 fw-bold text-dark small">
                                                {formatCurrency(order.totals?.total, currency)}
                                            </td>
                                            <td className="py-3">
                                                {getStatusBadge(order.status)}
                                            </td>
                                            <td className="pe-4 py-3 text-end">
                                                <LuChevronRight size={18} className="text-muted" />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <LuPackage size={48} className="text-muted opacity-25 mb-3" />
                            <h6 className="text-muted">No orders found</h6>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {pagination?.pages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <Pagination>
                        <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)} />
                        {[...Array(pagination.pages)].map((_, i) => (
                            <Pagination.Item
                                key={i + 1}
                                active={i + 1 === page}
                                onClick={() => setPage(i + 1)}
                            >
                                {i + 1}
                            </Pagination.Item>
                        ))}
                        <Pagination.Next disabled={page === pagination.pages} onClick={() => setPage(page + 1)} />
                    </Pagination>
                </div>
            )}
        </div>
    );
};

export default OrderList;
